// 模板管理器
export interface TemplateData {
  [key: string]: string | number | Array<Record<string, unknown>>;
}

// 模板類型
export type TemplateType = 'quote';

// 模板管理器類別
export class TemplateManager {
  private static templates: Map<TemplateType, string> = new Map();

  // 載入模板
  static async loadTemplate(type: TemplateType): Promise<string> {
    if (this.templates.has(type)) {
      return this.templates.get(type)!;
    }

    try {
      let templatePath: string;
      
      switch (type) {
        case 'quote':
          templatePath = '/templates/quoteTemplate.html';
          break;
        default:
          throw new Error(`不支援的模板類型: ${type}`);
      }

      const response = await fetch(templatePath);
      if (!response.ok) {
        throw new Error(`無法載入模板: ${response.statusText}`);
      }

      const template = await response.text();
      this.templates.set(type, template);
      return template;
    } catch (error) {
      throw new Error(`載入模板失敗: ${error}`);
    }
  }

  // 渲染模板
  static renderTemplate(template: string, data: TemplateData): string {
    let rendered = template;

    // 處理簡單的變數替換
    Object.keys(data).forEach(key => {
      const value = data[key];
      if (typeof value === 'string' || typeof value === 'number') {
        const regex = new RegExp(`{{${key}}}`, 'g');
        const replacement = String(value);
        rendered = rendered.replace(regex, () => replacement);
      }
    });

    // 處理陣列項目 (例如 items)
    const itemsMatch = rendered.match(/\{\{#items\}\}([\s\S]*?)\{\{\/items\}\}/);
    if (itemsMatch && Array.isArray(data.items)) {
      const itemTemplate = itemsMatch[1];
      const itemsHtml = data.items.map(item => {
        let itemHtml = itemTemplate;
        Object.keys(item).forEach(key => {
          const regex = new RegExp(`{{${key}}}`, 'g');
          const replacement = String(item[key] ?? '');
          itemHtml = itemHtml.replace(regex, () => replacement);
        });
        return itemHtml;
      }).join('');
      
      rendered = rendered.replace(/\{\{#items\}\}[\s\S]*?\{\{\/items\}\}/, () => itemsHtml);
    }

    // 處理報價單的特殊變數
    // SUMMARY_ROWS
    if (data.SUMMARY_ROWS) {
      const summaryRows = String(data.SUMMARY_ROWS);
      rendered = rendered.replace(/\{\{SUMMARY_ROWS\}\}/g, () => summaryRows);
    }

    // DETAIL_ROWS
    if (data.DETAIL_ROWS) {
      const detailRows = String(data.DETAIL_ROWS);
      rendered = rendered.replace(/\{\{DETAIL_ROWS\}\}/g, () => detailRows);
    }

    return rendered;
  }

  // 格式化貨幣
  static formatCurrency(amount: number): string {
    return `NT$ ${amount.toLocaleString()}`;
  }

  // 格式化日期
  static formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '/');
  }

  // 清理模板快取
  static clearCache(): void {
    this.templates.clear();
  }
}

// 報價單模板資料介面
export interface QuoteTemplateData extends TemplateData {
  PROJECT_NAME: string;
  QUOTE_NUMBER: string;
  VALID_UNTIL: string;
  CUSTOMER_NAME: string;
  CUSTOMER_TAX_ID: string;
  CONTACT_NAME: string;
  CONTACT_EMAIL: string;
  CREATOR_NAME: string;
  CREATOR_EMAIL: string;
  SUMMARY_ROWS: string;
  SUMMARY_AMOUNT_HEADER: string;
  DETAIL_HEAD: string;
  DETAIL_ROWS: string;
  DISCOUNTED_TOTAL_LABEL: string;
  TOTAL_AMOUNT_LABEL: string;
  SUBTOTAL: string;
  TOTAL_DISCOUNT: string;
  HOURS_TOTAL: string;
  PROCUREMENT_TOTAL: string;
  DISCOUNTED_TOTAL: string;
  TAX_AMOUNT: string;
  TOTAL_AMOUNT: string;
  TERMS: string;
  STAMP_BASE64: string;
  SIGN_DATE: string;
} 